import { useCallback } from "react";
import { useRouter } from "expo-router";
import { View, Text } from "@src/components";
import { useAlarm } from "@src/services/api/hooks";
import { useTenantId } from "@src/features/tenants";
import { WebRightPanel } from "./WebRightPanel.web";
import { Loading } from "@src/components/Loading";
import { deriveAlarmSettingsViewState } from "@src/features/alarms/utils/alarm-settings-view-state";
import { AlarmConfigSections } from "@src/features/alarms/components/AlarmConfigSections";
import { buildAlarmRoutePaths } from "@src/features/alarms/utils/alarm-routes";
import { useCameraPermissions } from "@visioni/a3s-access/app";
import { track } from "@visioni/a3s-analytics";
import { AlarmResponseOpened, AlarmRoutineOpened } from "@src/features/alarms/analytics";
import { cameraScope } from "@src/features/cameras/analytics";

interface TimetableSidePanelProps {
  cameraKey: string;
  siteId: string;
}

export function TimetableSidePanel({ cameraKey, siteId }: TimetableSidePanelProps) {
  const router = useRouter();
  const tenantId = useTenantId();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const routes = buildAlarmRoutePaths({ tenantId, siteId, cameraKey });
  const { canManageAlarmSchedules } = useCameraPermissions(siteId, cameraKey);
  const readOnly = !canManageAlarmSchedules;

  const { data: alarm, isLoading } = useAlarm(siteId, cameraKey);

  const handleSchedulePress = useCallback(() => {
    track(AlarmRoutineOpened, { ...cameraScope(tenantId, siteId, cameraKey), target: "routine" });
    router.push(routes.routine as never);
  }, [router, routes.routine, tenantId, siteId, cameraKey]);

  const handleExceptionPress = useCallback(
    (exceptionId: string) => {
      router.push(routes.exception(exceptionId) as never);
    },
    [router, routes],
  );

  const handleAddException = useCallback(() => {
    router.push(routes.addException as never);
  }, [router, routes.addException]);

  const handleResponsePress = useCallback(() => {
    track(AlarmResponseOpened, cameraScope(tenantId, siteId, cameraKey));
    router.push(routes.response as never);
  }, [router, routes.response, tenantId, siteId, cameraKey]);

  const handleTimezonePress = useCallback(() => {
    router.push(routes.timezone as never);
  }, [router, routes.timezone]);

  if (isLoading) {
    return (
      <WebRightPanel title="Timetable" subtitle="Edit your schedule and any exceptions.">
        <View flex={1} alignItems="center" justifyContent="center" py="6xl">
          <Loading />
        </View>
      </WebRightPanel>
    );
  }

  const routine = alarm?.settings?.routine ?? null;
  const exceptions = alarm?.settings?.exceptions ?? [];
  const response = alarm?.settings?.response ?? null;
  const { pristine, timezone, firstScheduleApplying, schedulesDiffer, cameraTimezone, showTimezoneMismatchBanner } =
    alarm ?
      deriveAlarmSettingsViewState({
        alarmData: alarm,
        userTimezone,
      })
    : {
        pristine: false,
        timezone: "UTC",
        firstScheduleApplying: false,
        schedulesDiffer: false,
        cameraTimezone: "Not set",
        showTimezoneMismatchBanner: false,
      };

  return (
    <WebRightPanel
      title="Timetable"
      subtitle={readOnly ? "View your schedule and exceptions." : "Edit your schedule and any exceptions."}
    >
      <AlarmConfigSections
        variant="web-sidepanel"
        pristine={pristine}
        firstScheduleApplying={firstScheduleApplying}
        schedulesDiffer={schedulesDiffer}
        showTimezoneMismatchBanner={showTimezoneMismatchBanner}
        timezone={timezone}
        cameraTimezone={cameraTimezone}
        userTimezone={userTimezone}
        routine={routine}
        exceptions={exceptions}
        response={response}
        onTimezonePress={handleTimezonePress}
        onRoutinePress={handleSchedulePress}
        onAddExceptionPress={handleAddException}
        onExceptionPress={handleExceptionPress}
        onResponsePress={handleResponsePress}
        onAppliedSchedulePress={() => router.push(routes.appliedSchedule as never)}
        readOnly={readOnly}
      />
    </WebRightPanel>
  );
}
